import { Link } from "react-router-dom";

// Parse inline markdown: **bold** and [text](url)
const renderInline = (text, keyBase = "i") => {
    if (!text) return null;
    const nodes = [];
    let remaining = text;
    let cursor = 0;
    const pattern = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
    let match;
    let lastIdx = 0;
    while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIdx) {
            nodes.push(text.slice(lastIdx, match.index));
        }
        if (match[1] !== undefined) {
            nodes.push(
                <strong key={`${keyBase}-b-${cursor++}`} className="text-brand-mist font-medium">
                    {match[1]}
                </strong>
            );
        } else {
            const linkText = match[2];
            const url = match[3];
            if (url === "#") {
                nodes.push(
                    <Link key={`${keyBase}-l-${cursor++}`} to="/contact" className="text-brand-primary underline underline-offset-4 hover:text-brand-mist transition-colors">
                        {linkText}
                    </Link>
                );
            } else if (url.startsWith("/")) {
                nodes.push(
                    <Link key={`${keyBase}-l-${cursor++}`} to={url} className="text-brand-primary underline underline-offset-4 hover:text-brand-mist transition-colors">
                        {linkText}
                    </Link>
                );
            } else {
                nodes.push(
                    <a key={`${keyBase}-l-${cursor++}`} href={url} target="_blank" rel="noopener noreferrer" className="text-brand-primary underline underline-offset-4 hover:text-brand-mist transition-colors">
                        {linkText}
                    </a>
                );
            }
        }
        lastIdx = match.index + match[0].length;
    }
    if (lastIdx < text.length) {
        nodes.push(text.slice(lastIdx));
    }
    remaining = remaining; // satisfy linter
    return nodes;
};

// Parse markdown body into block nodes.
export const renderMarkdown = (body, opts = {}) => {
    if (!body) return null;
    const lines = body.replace(/\r\n/g, "\n").split("\n");
    const blocks = [];
    let buffer = [];
    let inList = false;

    const flushParagraph = () => {
        if (buffer.length === 0) return;
        blocks.push({ type: "p", lines: [...buffer] });
        buffer = [];
    };
    const flushList = () => {
        if (buffer.length === 0) return;
        blocks.push({ type: "ul", items: [...buffer] });
        buffer = [];
        inList = false;
    };

    for (const raw of lines) {
        const line = raw.trim();
        if (line === "") {
            if (inList) flushList();
            else flushParagraph();
            continue;
        }
        if (line.startsWith("## ")) {
            if (inList) flushList();
            else flushParagraph();
            blocks.push({ type: "h2", text: line.replace(/^##\s+/, "") });
            continue;
        }
        const imgMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
        if (imgMatch) {
            if (inList) flushList();
            else flushParagraph();
            blocks.push({ type: "img", alt: imgMatch[1], src: imgMatch[2] });
            continue;
        }
        if (/^[-*]\s+/.test(line)) {
            if (!inList) {
                flushParagraph();
                inList = true;
            }
            buffer.push(line.replace(/^[-*]\s+/, ""));
            continue;
        }
        if (inList) flushList();
        buffer.push(line);
    }
    if (inList) flushList();
    else flushParagraph();

    return blocks.map((b, i) => {
        if (b.type === "h2") {
            return (
                <h2 key={i} className="font-display text-2xl lg:text-3xl tracking-tight text-brand-mist mt-14 mb-5">
                    {b.text}
                </h2>
            );
        }
        if (b.type === "img") {
            return (
                <figure key={i} className="my-10 border border-brand-primary/10">
                    <img src={b.src} alt={b.alt} className="w-full h-auto block" loading="lazy" />
                    {b.alt ? (
                        <figcaption className="px-4 py-3 text-xs uppercase tracking-widest text-brand-primary/70 bg-brand-surface">
                            {b.alt}
                        </figcaption>
                    ) : null}
                </figure>
            );
        }
        if (b.type === "ul") {
            return (
                <ul key={i} className="my-6 space-y-3 list-none">
                    {b.items.map((it, j) => (
                        <li key={j} className="flex gap-4 text-lg text-brand-frost/85 leading-relaxed">
                            <span className="mt-3 h-px w-4 shrink-0 bg-brand-primary/60" />
                            <span>{renderInline(it, `${i}-${j}`)}</span>
                        </li>
                    ))}
                </ul>
            );
        }
        return (
            <p key={i} className="text-lg text-brand-frost/85 leading-relaxed mb-6">
                {renderInline(b.lines.join(" "), `${i}`)}
            </p>
        );
    });
};
